import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import {
  trackUserAction,
  type TalentMatchingAttribution,
  type TrackedFilter,
} from 'talent_matching/lib/tracker';

// Form keys that are not filters (pagination / display prefs). Changes to these
// must not emit a `filters_applied` event.
const NON_FILTER_KEYS = ['page', 'perPage', 'page_size', 'user_date_format'];

function isEmptyValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    value === false ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Talent Matching filters apply reactively (the table refetches as fields
 * change; there is no explicit "Apply" button). This hook emits a single
 * `filters_applied` user-action event once the filter set "settles"
 * (debounced), carrying the non-empty filter values. Pagination changes and the
 * initial render are intentionally ignored, and an all-empty filter set (e.g.
 * after Clear All) is skipped — that is tracked separately as `filters_cleared`.
 *
 * Pass a memoized `attribution` so the debouncer is stable across renders.
 */
export function useTrackFiltersApplied(
  attribution: TalentMatchingAttribution,
  formValues: Record<string, unknown>,
  debounceMs = 1000
): void {
  const filters = useMemo<TrackedFilter[]>(() => {
    return Object.entries(formValues)
      .filter(
        ([key, value]) => !NON_FILTER_KEYS.includes(key) && !isEmptyValue(value)
      )
      .map(([name, values]) => ({ name, values }));
  }, [formValues]);

  const signature = JSON.stringify(filters);

  const emit = useMemo(
    () =>
      debounce((appliedFilters: TrackedFilter[]) => {
        trackUserAction({
          ...attribution,
          category: 'interaction',
          action: 'filters_applied',
          filters: appliedFilters,
        });
      }, debounceMs),
    [attribution, debounceMs]
  );

  // Seed with the initial signature so the first render does not emit.
  const previousSignature = useRef(signature);

  useEffect(() => {
    if (previousSignature.current === signature) {
      return;
    }
    previousSignature.current = signature;

    if (filters.length === 0) {
      // Filters cleared before the debounce fired: drop any pending emit so a
      // stale `filters_applied` can't land after the `filters_cleared` event.
      emit.cancel();
      return;
    }
    emit(filters);
  }, [signature, filters, emit]);

  useEffect(() => () => emit.cancel(), [emit]);
}
